import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  TextFieldProps,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { DynamicContent, Pagination } from 'shared/components'
import { PaginationProps } from 'shared/hooks'
import { AddActionOption } from '../types'
import {
  Container,
  ErrorWrapper,
  ListWrapper,
  PaginationWrapper,
  SearchWrapper,
} from './SingleSelectDialogList.style'

export interface SingleSelectDialogListError {
  message: string
  retryLabel: string
  onRetry: () => void
}

type SingleSelectDialogListSearchProps = Pick<
  TextFieldProps,
  'placeholder' | 'fullWidth'
>

type SingleSelectDialogListPaginationProps = PaginationProps & {
  visibleLabel?: boolean
}

interface SingleSelectDialogListProps {
  query: string
  setQuery: (value: string) => void
  dataLoading: boolean
  actionLoading?: boolean
  options: AddActionOption[]
  paginationProps: SingleSelectDialogListPaginationProps
  onItemClick: (item: AddActionOption) => void
  selectedValue?: string
  searchProps?: SingleSelectDialogListSearchProps
  error?: SingleSelectDialogListError
}

const SingleSelectDialogList = ({
  query,
  setQuery,
  dataLoading,
  actionLoading = false,
  options,
  paginationProps,
  onItemClick,
  selectedValue,
  searchProps,
  error,
}: SingleSelectDialogListProps) => {
  const { t } = useTranslation()

  return (
    <Container>
      <SearchWrapper>
        <TextField
          value={query}
          onChange={event => setQuery(event.target.value)}
          size="small"
          {...searchProps}
          disabled={actionLoading}
          InputProps={{
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  aria-label={t('clear')}
                  onClick={() => setQuery('')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </SearchWrapper>

      <ListWrapper>
        {error && !dataLoading ? (
          <ErrorWrapper>
            <Alert
              severity="error"
              action={
                <Button
                  type="button"
                  color="inherit"
                  size="small"
                  onClick={error.onRetry}
                >
                  {error.retryLabel}
                </Button>
              }
            >
              {error.message}
            </Alert>
          </ErrorWrapper>
        ) : (
          <DynamicContent
            loading={dataLoading}
            textAlign="center"
            displayContent={options.length}
          >
            <List disablePadding>
              {options.map(item => (
                <ListItemButton
                  key={String(item.value)}
                  selected={item.value === selectedValue}
                  disabled={item.disabled || dataLoading || actionLoading}
                  onClick={() => onItemClick(item)}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </DynamicContent>
        )}
      </ListWrapper>

      <PaginationWrapper>
        <Pagination {...paginationProps} />
      </PaginationWrapper>
    </Container>
  )
}

export default SingleSelectDialogList
